import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import GlassCard from "../components/GlassCard";
import PrimaryButton from "../components/PrimaryButton";
import BottomNav from "../components/BottomNav";
import PageShell from "../components/PageShell";
import { Plus, Search } from "lucide-react";

const Sessions = () => {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/sessions").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((s) => {
      const name = (s.client_name || "").toLowerCase();
      const preview = s.source_type === "ai_listen"
        ? (s.sections?.session_overview || "").toLowerCase()
        : (s.notes_content || "").toLowerCase();
      return name.includes(q) || preview.includes(q);
    });
  }, [items, query]);

  return (
    <PageShell showSettings>
      <div className="pt-16 pb-32 max-w-2xl mx-auto flex flex-col gap-6 well-page-enter">
        <h1 className="text-3xl font-bold text-[#2A3B32] text-center">Your sessions</h1>

        <PrimaryButton onClick={() => nav("/new-session")} data-testid="sessions-new-btn">
          <Plus size={20} className="mr-2" /> Start New Session
        </PrimaryButton>

        {items.length > 0 && (
          <div className="relative" data-testid="sessions-search-wrap">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A9C91]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by client name or note…"
              data-testid="sessions-search-input"
              className="w-full h-[48px] pl-11 pr-4 bg-white/60 backdrop-blur-sm border border-white/40 text-[#2A3B32] rounded-[16px] focus:outline-none focus:ring-2 focus:ring-[#8BA38E]"
            />
          </div>
        )}

        {items.length === 0 && (
          <GlassCard className="text-center" data-testid="sessions-empty">
            <p className="text-[#4A5D52]">No sessions yet. Tap Start New Session to get going.</p>
          </GlassCard>
        )}

        {items.length > 0 && filtered.length === 0 && (
          <GlassCard className="text-center" data-testid="sessions-no-results">
            <p className="text-[#4A5D52]">No sessions match &quot;{query}&quot;.</p>
          </GlassCard>
        )}

        {filtered.map((s) => {
          const isDraft = s.status === "draft";
          const preview = s.source_type === "ai_listen"
            ? (s.sections?.session_overview || "")
            : (s.notes_content || "");
          return (
            <button
              key={s.session_id}
              onClick={() => nav(isDraft ? `/review/${s.session_id}` : `/results/${s.session_id}`)}
              className="text-left"
              data-testid={`sessions-item-${s.session_id}`}
            >
              <GlassCard className="hover:bg-white/55 transition-colors">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-[#2A3B32]">{s.client_name}</p>
                  <div className="flex items-center gap-2">
                    {isDraft ? (
                      <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E0B96A]/25 text-[#8B6E1F] border border-[#E0B96A]/40">
                        Draft
                      </span>
                    ) : (
                      <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#8BA38E]/20 text-[#6A7F6C] border border-[#8BA38E]/30">
                        Sent
                      </span>
                    )}
                    <p className="text-xs text-[#8A9C91]">
                      {new Date(s.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <p className="mt-2 text-sm text-[#4A5D52] line-clamp-2">
                  {preview.slice(0, 160)}{preview.length > 160 ? "…" : ""}
                </p>
                <p className="mt-2 text-[11px] text-[#8A9C91]">
                  {s.source_type === "ai_listen" ? "AI Listen In" : "Written Notes"}
                </p>
              </GlassCard>
            </button>
          );
        })}
      </div>
      <BottomNav />
    </PageShell>
  );
};

export default Sessions;
